/**
 * Locking API Client
 * --------------------------------------------------
 * Client for the resource locking API endpoints.
 * Provides optimistic locking with lease-based expiry and fencing tokens.
 */

import { AGWASuiteApiClientBase, ApiResponse } from '../agwasuite-api';
import { ApiFactory } from '../agwasuite-api-factory';
import type { KeptText } from '../../language/kept-text-value';

const BASE_PATH = '/api/locks';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface AcquireLockInput {
  resourceType: string;
  resourceId: string;
  leaseDurationSeconds?: number;
}

export interface RefreshLockInput {
  resourceType: string;
  resourceId: string;
  leaseDurationSeconds?: number;
}

export interface ReleaseLockInput {
  resourceType: string;
  resourceId: string;
}

export interface SubscribeLockTopicInput {
  resourceType: string;
  resourceId?: string;
}

export interface LockHolderDto {
  userId: string;
  /**
   * The session that HOLDS the lock, as reported by the server. Inbound only: this is how a client
   * decides whether the holder is itself. Unlike the request shapes above, which no longer carry a
   * session because the server proves the caller's own from the X-AGWA-Tab-Session header, this one
   * is information the server is giving out (#366).
   */
  sessionId: string;
  // No name: the holder is named through the directory by userId (D-162, #398 C12).
  leaseExpiresAt: string;
  acquiredAt: string;
}

export interface LockResult {
  success: boolean;
  errorMessage?: string;
  fencingToken?: number;
  leaseExpiresAt?: string;
  currentHolder?: LockHolderDto;
  /**
   * Client-side only, never sent by the server: set by the lock store when the request got no
   * answer (D-144), so a page asks `isUnanswered` of the lock result as of any API result.
   */
  unanswered?: true;
  /**
   * Client-side only, never sent by the server: set by the lock store when `errorMessage` is the
   * API client's resolved failure text, carrying that text as the server sent it, so the error toast
   * resolves it once and shows a person it names by their current name (@VOC-SERVER-002).
   */
  errorKept?: KeptText;
}

export interface ResourceLockDto {
  resourceType: string;
  resourceId: string;
  ownerUserId: string;
  ownerSessionId: string;
  leaseExpiresAt: string;
  fencingToken: number;
  acquiredAt: string;
  lastRefreshedAt: string;
  /** Computed client-side */
  isActive?: boolean;
}

export interface BoolResult {
  value: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// API Client
// ─────────────────────────────────────────────────────────────────────────────

export class LockingApi extends AGWASuiteApiClientBase {
  /**
   * Acquires a lock on a resource.
   * If the lock is expired or owned by the same session, it will be taken over.
   * The fencing token is incremented on each acquire (not refresh).
   */
  async acquire(input: AcquireLockInput): Promise<ApiResponse<LockResult>> {
    return this.post<AcquireLockInput, LockResult>(
      `${BASE_PATH}/acquire`,
      input
    );
  }

  /**
   * Refreshes an existing lock, extending the lease.
   * Only the owner session can refresh. Does not increment the fencing token.
   */
  async refresh(input: RefreshLockInput): Promise<ApiResponse<LockResult>> {
    return this.post<RefreshLockInput, LockResult>(
      `${BASE_PATH}/refresh`,
      input
    );
  }

  /**
   * Releases a lock. Only the owner session can release.
   */
  async release(input: ReleaseLockInput): Promise<ApiResponse<LockResult>> {
    return this.post<ReleaseLockInput, LockResult>(
      `${BASE_PATH}/release`,
      input
    );
  }

  /**
   * Gets the current lock status for a resource.
   */
  async getStatus(
    resourceType: string,
    resourceId: string
  ): Promise<ApiResponse<ResourceLockDto | null>> {
    const params = new URLSearchParams({
      resourceType,
      resourceId,
    });
    return this.get<ResourceLockDto | null>(`${BASE_PATH}/status?${params}`);
  }

  /**
   * Subscribes to lock events for a resource type (and optionally a specific resource).
   * This subscribes the session's SSE connection to receive lock events.
   */
  async subscribe(
    input: SubscribeLockTopicInput
  ): Promise<ApiResponse<BoolResult>> {
    return this.post<SubscribeLockTopicInput, BoolResult>(
      `${BASE_PATH}/subscribe`,
      input
    );
  }

  /**
   * Unsubscribes from lock events for a resource type (and optionally a specific resource).
   */
  async unsubscribe(
    input: SubscribeLockTopicInput
  ): Promise<ApiResponse<BoolResult>> {
    return this.post<SubscribeLockTopicInput, BoolResult>(
      `${BASE_PATH}/unsubscribe`,
      input
    );
  }
}

/**
 * Get a locking API instance.
 * Creates a fresh instance each call to ensure current auth/CSRF tokens.
 */
export function getLockingApi(): LockingApi {
  return ApiFactory.authenticated(LockingApi);
}
